// All user-facing copy lives here so the UI can be translated later without touching components.
// Plain language for non-technical readers: short sentences, no API or Keycloak vocabulary.

export const strings = {
  appName: 'Knowledge Base',
  skipToContent: 'Skip to main content',

  signIn: {
    title: 'Sign in to the Knowledge Base',
    intro: 'Which organisation are you with? You will sign in with your usual organisation account.',
    searchLabel: 'Find your organisation',
    searchPlaceholder: 'Type part of the name',
    noMatch: 'No organisation matches that name. Check the spelling, or ask the Knowledge Base operator to add yours.',
    continue: 'Continue',
    remembered: (name: string) => `Last time you signed in with ${name}.`,
    changeOrganisation: 'Change organisation',
    redirecting: 'Taking you to your organisation’s sign-in page…',
    failed: (contact: string) =>
      `We couldn’t finish signing you in. Please try again; if it keeps happening, contact ${contact}.`,
  },

  header: {
    signOut: 'Sign out',
    signedInAs: (name: string) => `Signed in as ${name}`,
    switchPilot: 'Switch pilot',
  },

  home: {
    choosePilot: 'Choose a pilot',
    lastViewed: 'Last viewed',
    choosePilotIntro: 'You have access to more than one pilot. Pick the one you want to look at.',
    noPilots: (contact: string) =>
      `Your account is signed in, but your organisation hasn’t been given access to any pilot yet. Please contact ${contact}.`,
    searchTitle: 'Search this pilot',
    recent: 'Recently updated',
    topics: 'Topics',
    topicItems: (n: number) => (n === 1 ? '1 item' : `${n} items`),
    noTopics: 'There are no topics in this pilot yet.',
    viewAll: 'See everything',
  },

  search: {
    title: 'Search',
    label: 'What are you looking for?',
    placeholder: 'For example: flood, river levels, reservoir',
    submit: 'Search',
    filters: 'Narrow down',
    kind: 'Kind of item',
    pilot: 'Pilot',
    sector: 'Sector',
    sensitivity: 'Sensitivity',
    sort: 'Sort by',
    sortUpdated: 'Recently updated',
    sortCreated: 'Recently added',
    sortName: 'Name (A–Z)',
    any: 'Any',
    clear: 'Clear filters',
    results: (n: number) => (n === 1 ? '1 result' : `${n} results`),
    none: 'Nothing found. Try fewer words, a different spelling, or clear the filters.',
    datasetOnlyHint: 'Pilot, sector and sensitivity only apply to datasets.',
    updated: (date: string) => `Updated ${date}`,
    custodian: (name: string) => `Looked after by ${name}`,
  },

  pagination: {
    previous: 'Previous page',
    next: 'Next page',
    page: (page: number, total: number) => `Page ${page} of ${total}`,
  },

  topic: {
    objectives: 'Objectives',
    requirements: 'Requirements',
    sequences: 'Learning paths and sequences',
    items: 'Items in this topic',
    empty: 'This topic has no items yet.',
  },

  item: {
    back: 'Back',
    whatIsIt: 'What is it?',
    whoLooksAfterIt: 'Who looks after it?',
    howSensitive: 'How sensitive is it?',
    coverage: 'What time and place does it cover?',
    quality: 'How reliable is it?',
    files: 'Files',
    noFiles: 'There are no files attached to this item.',
    download: 'Download',
    preview: 'Preview',
    hidePreview: 'Hide preview',
    technical: 'Technical details',
    technicalIntro: 'The full metadata record, for data specialists.',
    notStated: 'Not stated',
    personalData: 'Contains personal data',
    criticalInfrastructure: 'Contains critical-infrastructure data',
    from: 'From',
    to: 'to',
    updates: 'Updates',
    version: 'Version',
    contactCustodian: 'Email the custodian',
    datasetFile: 'Dataset file',
    payloadFile: 'Data file',
    attachedFile: 'Attached file',
    previewTooLarge: 'This file is too large to preview. Download it to open it.',
    previewRows: (n: number) => `First ${n} rows`,
  },

  download: {
    confirmTitle: 'Handle this file with care',
    confirmBody: (level: string) =>
      `This item is marked “${level}”. Only download it if you need it for your work, keep it on approved systems, and don’t share it outside the people allowed to see it.`,
    confirm: 'I understand, download',
    cancel: 'Cancel',
    failed: 'The download could not be started. Please try again in a moment.',
  },

  errors: {
    title: 'Something went wrong',
    sessionExpired: 'Your session has ended. Please sign in again.',
    forbidden: (contact: string) =>
      `Your organisation hasn’t been given access to this. If you think you should have it, contact ${contact}.`,
    notFound: 'We couldn’t find that. It may have been removed, or the link may be wrong.',
    network: 'We couldn’t reach the Knowledge Base. Check your connection and try again.',
    generic: 'The Knowledge Base had a problem answering. Please try again in a moment.',
    config: 'The Knowledge Base website is not set up correctly. Please tell the operator.',
    retry: 'Try again',
    signInAgain: 'Sign in again',
  },

  loading: 'Loading…',
  notFoundPage: {
    title: 'Page not found',
    body: 'That page doesn’t exist.',
    home: 'Go to the start page',
  },
};
